import { useEffect, useMemo, useRef } from 'react';

/**
 * "Is the work I started still the work anyone wants?"
 *
 * Every screen that fetches has to answer that after each `await`, because a
 * reply can outlive the thing that asked for it: the player navigated away,
 * signed into a different account, or simply asked again and the second answer
 * came back first. Writing the answer into a newer request's state is how a
 * stale screen looks like a fresh one.
 *
 * The discipline is in `.claude/rules/ts/async_lifecycles.md`; this is the seam
 * that makes it one decision instead of six. It replaced three spellings of the
 * same idea — `let cancelled = false`, `let current = true`, and a bare
 * `useRef(0)` generation — two of which had opposite polarity, so the nearest
 * example to copy was as likely to be backwards as right.
 *
 * ```ts
 * const request = useLatestRequest();
 *
 * useEffect(() => {
 *   const isCurrent = request.begin();
 *   void load().then((data) => {
 *     if (isCurrent()) setData(data);
 *   });
 *   return request.retire;
 * }, [request, dependency]);
 * ```
 *
 * `begin()` returns the test rather than exposing the counter, so a call site
 * cannot read the generation and compare it wrongly. A caller that re-runs
 * without unmounting — a "load more" button, say — calls `begin()` again, and
 * the previous run's `isCurrent()` starts answering false from that moment.
 */
export interface LatestRequest {
  /** Claims ownership for the work starting now; returns "still mine?". */
  readonly begin: () => () => boolean;
  /**
   * Retires whatever is in flight, so no outstanding `isCurrent()` answers
   * true again. Safe to pass straight to an effect as its cleanup, which is
   * why both of these are function properties rather than methods: a bare
   * `request.retire` has to survive being separated from its object.
   */
  readonly retire: () => void;
}

export function useLatestRequest(): LatestRequest {
  const generation = useRef(0);
  // Identity has to be stable: these land in effect and callback dependency
  // arrays, and a new object every render would re-run the very fetches this
  // exists to keep in order.
  const request = useMemo<LatestRequest>(
    () => ({
      begin: () => {
        const mine = ++generation.current;
        return () => generation.current === mine;
      },
      retire: () => {
        generation.current += 1;
      },
    }),
    [],
  );
  // Unmounting retires in-flight work too, so a reply that lands after the
  // screen is gone cannot set state on it.
  useEffect(() => request.retire, [request]);
  return request;
}
